import { useState, useEffect } from 'react';
import { authedApiFetch } from '../../utils/Api';
import {
	isRouteErrorResponse,
	useLoaderData,
	useParams,
	useRevalidator,
	useRouteError,
	useOutletContext,
} from 'react-router';
import {
	Alert,
	Box,
	Button,
	IconButton,
	MenuItem,
	Paper,
	Select,
	Snackbar,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { LoadingButton } from '@mui/lab';
import CircularProgress from '@mui/material/CircularProgress';
import NotificationBox from './components/NotificationBox';
import StatusToggle from './components/StatusToggle';

export async function clientLoader({ params }) {
	const { tenantId } = params;

	// Fetch both config and available objects
	const configPromise = authedApiFetch({
		endpoint: `/tenants/${tenantId}/aime/config`,
		method: 'GET',
		returnResponse: true,
	});

	const availableObjectsPromise = authedApiFetch({
		endpoint: `/tenants/${tenantId}/aime/config/available_objects`,
		method: 'GET',
		returnResponse: true,
	});

	try {
		const [configResponse, availableObjectsResponse] = await Promise.all([
			configPromise,
			availableObjectsPromise,
		]);

		// Check for config 404 and throw 404 response which goes to error boundary
		// See: https://reactrouter.com/6.30.0/route/error-element#throwing-responses
		if (configResponse.status === 404) {
			throw new Response('Not Found', { status: 404 });
		}

		// Check if configResponse is not OK (but not 404)
		if (!configResponse.ok) {
			console.error('Error retrieving AIMe Configuration:', configResponse.status, configResponse.statusText);
			return {
				config: null,
				availableObjects: [],
				configError: true,
			};
		}

		// Parse the config body as JSON
		const configBody = await configResponse.json();

		// Handle available objects response
		const availableObjectsBody = await availableObjectsResponse.json();
		const availableObjects = availableObjectsBody.objects || [];

		if (!availableObjectsResponse.ok && availableObjectsBody.error) {
			// Log errors if the response is not OK
			console.error('Error loading available objects:', availableObjectsBody.error, 
				`Username: ${availableObjectsBody.username || 'Not found'}`);
		}

		return {
			config: configBody,
			availableObjects: availableObjects,
			availableObjectsError: availableObjectsBody.error,
		};
	} catch (error) {
		console.error('Error in AimeConfig loader:', error);
		
		// If the config fetch fails but it's not a 404, we'll show a generic error
		if (error instanceof Response && error.status === 404) {
			// Let the 404 error propagate to the error boundary
			throw error;
		}

		// For other errors, we'll return a partial result that the component can handle
		return {
			config: null,
			availableObjects: [],
			loaderErrorMessage: error.message || 'Failed to load configuration data',
		};
	}
}

// Error boundary for 404 responses
export function ErrorBoundary() {
	let error = useRouteError();

	if (isRouteErrorResponse(error) && error.status === 404) {
		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					rowGap: '16px',
					height: 'calc(100vh - 128px)',
					paddingTop: '12px',
				}}
			>
				<Typography variant='h4'>AIMe Configuration</Typography>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						height: '100%',
					}}
				>
					AIMe configuration not found for tenant.
				</Box>
			</Box>
		);
	} else {
		// re-throw to let parent error boundary handle it
		throw error;
	}
}

function AimeConfig() {
	const { tenantId } = useParams();
	const { config, configError, availableObjects, availableObjectsError } = useLoaderData();
	const revalidator = useRevalidator();

	const { tenant, user } = useOutletContext(); // Still need tenant and user for UI
	
	// Check if AIMe is enabled for the tenant
	const isAimeEnabled = tenant?.aime_enabled === true;
	
	// Check if user has admin privileges to toggle AIMe status
	const isAltviaAdminOrPowerUser = user?.role === 'altvia_admin' || user?.role === 'altvia_power_user';
	const isInternalUser = isAltviaAdminOrPowerUser || user?.role === 'altvia_user';
	
	// State for tracking AIMe enabled status (initialized from tenant)
	const [aimeEnabled, setAimeEnabled] = useState(isAimeEnabled);

	// State for tracking loading state during toggle and revalidation
	const [isLoading, setIsLoading] = useState(false);
	
	// Handle toggle change for AIMe status
	const handleAimeStatusChange = async (event, newValue) => {
		if (newValue !== null) {
			const newEnabledStatus = newValue === 'enabled';
			
			// Set loading state
			setIsLoading(true);
			
			// Update local state immediately for responsive UI
			setAimeEnabled(newEnabledStatus);
			
			// Update tenant's aime_enabled status via API
			try {
				const response = await authedApiFetch({
					endpoint: `/tenants/${tenantId}`,
					method: 'PATCH',
					payload: {
						aime_enabled: newEnabledStatus
					},
					returnResponse: true
				});
				
				if (!response.ok) {
					const errorData = await response.json();
					throw new Error(errorData.error || 'Failed to update AIMe status');
				}
				
				// Don't show success message for toggle operations
				setErrorMessage('');
				
				// Refresh data to ensure UI reflects current state
				try {
					revalidator.revalidate();
					
					// For disabling AIMe, we need to handle the case where revalidation might fail
					// but the toggle itself was successful
					if (!newEnabledStatus) {
						// Set a timeout to clear loading state if it's taking too long
						// This handles the case where revalidation might fail with 403s
						setTimeout(() => {
							setIsLoading(false);
						}, 2000); // 2 seconds timeout
					}
				} catch (revalidateError) {
					console.error('Error during revalidation:', revalidateError);
					// Clear loading state if revalidation fails
					setIsLoading(false);
				}
			} catch (error) {
				console.error('Failed to update AIMe status:', error);
				
				// Show error message
				setSaveError(true);
				setErrorMessage(error.message || 'Failed to update AIMe status');
				
				// Revert UI state if API call fails
				setAimeEnabled(isAimeEnabled);
				
				// Clear loading state on error
				setIsLoading(false);
			}
			// Note: We don't clear loading state here as we want it to remain during revalidation
		}
	};

	// State to track objects the user doesn't have access to
	const [inaccessibleObjects, setInaccessibleObjects] = useState([]);

	// Create initial rows from existing configuration or start with one empty row
	const createInitialRows = () => {
		// Check if config has salesforce_objects property and it's an array with items
		if (config?.configuration?.salesforce_objects?.length > 0) {
			// Get available object names for filtering
			const availableObjectNames = (availableObjects || []).map(obj => obj.name);
			
			// Find objects that aren't in the available objects list
			const missingObjects = config.configuration.salesforce_objects
				.filter(obj => !availableObjectNames.includes(obj.name));
			
			// Set the inaccessible objects for warning display
			if (missingObjects.length > 0) {
				setInaccessibleObjects(missingObjects);
			}
			
			// Filter out objects that aren't in the available objects list. This can
			// happen if the user doesn't have access to the object in their org.
			const filteredObjects = config.configuration.salesforce_objects
				.filter(obj => availableObjectNames.includes(obj.name));
			
			// Map filtered objects to row format
			return filteredObjects.map((obj) => ({
				objectName: obj.name,
				aliases: obj.aliases ? obj.aliases.join(', ') : '',
			}));
		}
		// Add one empty row
		return [{ objectName: '', aliases: '' }];
	};

	const [rows, setRows] = useState(createInitialRows);

	// Update rows when config changes (after revalidation)
	useEffect(() => {
		if (config) {
			// If we have any config response (even if it doesn't have salesforce_objects)
			if (config.configuration?.salesforce_objects) {
				setRows(createInitialRows());
			}
			// Clear loading state after revalidation completes
			setIsLoading(false);
		}
	}, [config]);

	const [isSaving, setIsSaving] = useState(false);
	const [saveSuccess, setSaveSuccess] = useState(false);
	const [saveError, setSaveError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	// Add a new empty row
	const addRow = () => {
		setRows([...rows, { objectName: '', aliases: '' }]);
	};

	// Remove a row by index
	const removeRow = (indexToRemove) => {
		setRows(rows.filter((_, index) => index !== indexToRemove));
	};

	// Handle changes to object selection
	const handleObjectChange = (index, newObjectName) => {
		const updatedRows = [...rows];
		updatedRows[index] = { ...updatedRows[index], objectName: newObjectName };
		setRows(updatedRows);
	};

	// Handle changes to aliases
	const handleAliasChange = (index, newAliases) => {
		const updatedRows = [...rows];
		updatedRows[index] = { ...updatedRows[index], aliases: newAliases };
		setRows(updatedRows);
	};

	// Save configuration
	const handleSave = async () => {
		setIsSaving(true);
		setSaveSuccess(false);
		setSaveError(false);

		// Transform rows to the format expected by the backend
		const salesforceObjects = rows
			.filter((row) => row.objectName.trim() !== '') // Filter out rows with empty object names
			.map((row) => ({
				name: row.objectName,
				// Split aliases by comma, trim each one, and filter out empty ones
				aliases: row.aliases
					.split(',')
					.map((alias) => alias.trim())
					.filter((alias) => alias !== ''),
			}));

		try {
			const response = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/aime/config`,
				method: 'PUT',
				payload: {
					configuration: {
						salesforce_objects: salesforceObjects,
					},
				},
				returnResponse: true,
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.error || 'Failed to save configuration');
			}

			setSaveSuccess(true);
			revalidator.revalidate(); // Refresh data
		} catch (error) {
			setSaveError(true);
			setErrorMessage(error.message || 'An error occurred while saving');
		} finally {
			setIsSaving(false);
		}
	};

	// Close success notification
	const handleCloseSuccess = () => {
		setSaveSuccess(false);
	};

	// Close error notification
	const handleCloseError = () => {
		setSaveError(false);
	};

	return (
		<Box
			position="relative"
			sx={{
				display: 'flex',
				flexDirection: 'column',
				rowGap: '16px',
				height: 'calc(100vh - 128px)',
				paddingTop: '12px',
			}}
		>
			{/* Loading spinner */}
			{isLoading && (
				<Box
					position="absolute"
					top="50%"
					left="50%"
					marginTop="-30px"
					marginLeft="-30px"
					zIndex={10}
				>
					<CircularProgress size={60} thickness={4} />
				</Box>
			)}
			<StatusToggle
				enabled={aimeEnabled}
				isLoading={isLoading}
				canToggleStatus={isAltviaAdminOrPowerUser}
				handleStatusChange={handleAimeStatusChange}
			/>

			{/* Show only one notification box at a time */}
			{(() => {
				// AIMe not enabled
				if (!isAimeEnabled) {
					let message = ""
					if (isInternalUser) {
						// 'altvia_user' accounts will just see this
						message = "This client doesn't have AIMe access."

						if (isAltviaAdminOrPowerUser) {
							// 'altvia_admin' and 'altvia_power_user' accounts will see this as well since they can toggle
							message += " Click the \"Enabled\" button in the top right corner of this page to enable AIMe for this tenant."
						}
					} else {
						// 'tenant_admin' accounts (clients) will see this
						message = "You're one step away from transforming your team with proactive, AI-powered insights right at your fingertips. Contact us to learn how to enable AIMe for your organization."
					}
					return (
						<NotificationBox 
							type="error"
							title="Unlimited AIMe Access"
							content={message}
							showContactButton={true}
							marginTop="20px"
							isInternalUser={isInternalUser}
						/>
					);
				}

				// Config error
				if (configError) {
					return (
						<NotificationBox 
							type="error"
							title="Error"
							content="There was an error retrieving the AIMe Configuration. Please try again or contact support."
						/>
					);
				}

				// Available objects error
				if (availableObjectsError) {
					return (
						<NotificationBox 
							type="warning"
							title="Additional permissions needed"
							content="AIMe is active, but we couldn&apos;t load the Salesforce objects. Please ensure your user has the necessary access or let us know and we&apos;ll help configure the right permissions."
							isInternalUser={isInternalUser}
						/>
					);
				}

				// Inaccessible objects
				if (inaccessibleObjects.length > 0) {
					return (
						<NotificationBox 
							type="warning"
							title="Limited Object Access"
							content={
								<>
									<Typography sx={{ color: '#000000' }}>
										Your user doesn&apos;t have access to all Salesforce objects stored in the current AIMe Configuration. 
										If you save changes, the following objects will be removed from your company&apos;s configuration:
									</Typography>
									<Box component="ul" sx={{ mt: 1, mb: 0, color: '#000000' }}>
										{inaccessibleObjects.map((obj) => (
											<li key={obj.name}>{obj.name}</li>
										))}
									</Box>
								</>
							}
							showContactButton={false}
							marginTop="0px"
							width="100%"
						/>
					);
				}

				// No notification to show
				return null;
			})()}

			{isAimeEnabled && !configError && !availableObjectsError && config && <Paper elevation={1} sx={{ p: 2 }}>
				<Typography variant='h6'>
					Salesforce Objects Configuration
				</Typography>

				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Salesforce Object</TableCell>
								<TableCell>Aliases (comma separated)</TableCell>
								<TableCell width='80px'>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map((row, index) => (
								<TableRow key={index}>
									<TableCell>
										<Select
											value={row.objectName}
											onChange={(e) =>
												handleObjectChange(index, e.target.value)
											}
											fullWidth
											displayEmpty
										>
											<MenuItem value='' disabled>
												Select an object
											</MenuItem>
											{(availableObjects || []).map((obj) => (
												<MenuItem key={obj.name} value={obj.name}>
													{obj.label} ({obj.name})
												</MenuItem>
											))}
										</Select>
									</TableCell>
									<TableCell>
										<TextField
											value={row.aliases}
											onChange={(e) => handleAliasChange(index, e.target.value)}
											fullWidth
											placeholder='Enter aliases separated by commas'
										/>
									</TableCell>
									<TableCell>
										<IconButton
											onClick={() => removeRow(index)}
											color='error'
											size='small'
										>
											<DeleteIcon />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>

				<Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
					<Button startIcon={<AddIcon />} onClick={addRow} variant='outlined'>
						Add Row
					</Button>

					<LoadingButton
						variant='contained'
						color='primary'
						onClick={handleSave}
						loading={isSaving}
					>
						Save Configuration
					</LoadingButton>
				</Box>
			</Paper>}

			{/* Success notification */}
			<Snackbar
				open={saveSuccess}
				autoHideDuration={6000}
				onClose={handleCloseSuccess}
			>
				<Alert onClose={handleCloseSuccess} severity='success'>
					Configuration saved successfully!
				</Alert>
			</Snackbar>

			{/* Error notification */}
			<Snackbar
				open={saveError}
				autoHideDuration={6000}
				onClose={handleCloseError}
			>
				<Alert onClose={handleCloseError} severity='error'>
					{errorMessage || 'Failed to save configuration'}
				</Alert>
			</Snackbar>
		</Box>
	);
}

export default AimeConfig;
